import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import LegacySelect from 'react-select-legacy';
import { useTranslation } from 'react-i18next';
import DateRange from './DateRange';
import EditButtonSearch from './EditButtonSearch';
import FilterCheckbox from './FilterCheckbox';
import SelectCheckbox from './SelectCheckbox';
import ReportDownload from '../helpers/ReportDownload';
import { isAdminDashboard, isChatEnabled } from '../../utils/authScopes';
import { accountPlayersColumnsOptions } from '../helpers/TableHelpers';
import { groupByDisabled } from '../../utils/select';

const AccountPlayersFilter = ({
  auth, meta, onCSVEnqueue, onUserActionsCSVEnqueue, onSearch, onArchivedChange,
  accounts, onAccountChange, filterOptions, subgroups, onSubgroupChange,
  cohorts, onCohortChange, groups, onGroupChange, csvReport, userActionsCSVReport, onReportDetailsShow,
  onUserActionsReportDetailsShow, onDatesChange, options, onColumnVisibilityChange,
  partners, onPartnerChange, ages, onAgeChange,
  onBulkSend, onUnreadChange, isLoading }) => {

  const { t } = useTranslation();
  const [q, setQ] = React.useState(meta.q);

  // Sync local state with meta.q when it changes externally (e.g., from URL or Redux)
  React.useEffect(() => {
    setQ(meta.q);
  }, [meta.q]);

  const handleSearchChange = (value) => {
    setQ(value);
    onSearch(value);
  };

  return (
    <div>
      <div className='row u-margin-top--base u-margin-bottom--large align-middle'>
        {isAdminDashboard(auth) &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <LegacySelect
              name='sp-partner-select'
              className={'o-select o-select--optgroup'}
              multi={true}
              value={meta.partners || ''}
              onChange={onPartnerChange}
              options={partners}
              placeholder={t('filters.allPartners')}
              simpleValue={true}
              disabled={isLoading}
            />
          </div>}
        {groups.length > 1 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-group-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={groups.filter(v => (meta.groups || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onGroupChange(v.map(i => i.value).join(','))}
              options={groups}
              placeholder={t('filters.allGroups')}
              isDisabled={isLoading}
            />
          </div>
        }
        {subgroups.length > 1 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-cohort-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={subgroups.filter(v => (meta.subgroups || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onSubgroupChange(v.map(i => i.value).join(','))}
              options={groupByDisabled(subgroups)}
              placeholder={t('filters.allSubgroups')}
              isDisabled={isLoading}
            />
          </div>
        }
        {cohorts.length > 1 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-cohort-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={cohorts.filter(v => (meta.cohorts || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onCohortChange(v.map(i => i.value).join(','))}
              options={groupByDisabled(cohorts)}
              placeholder={t('filters.allCohorts')}
              isDisabled={isLoading}
            />
          </div>
        }
        <div className='column small-12 medium-4 large-3 u-margin-top--base'>
          <LegacySelect
            name='sp-account-select'
            className={'o-select o-select--optgroup'}
            filterOptions={filterOptions}
            multi={true}
            value={meta.accounts || ''}
            onChange={onAccountChange}
            options={accounts}
            placeholder={t('accountPlayers.filters.allAdults')}
            simpleValue={true}
            disabled={isLoading}
          />
        </div>
        <div className='column small-12 medium-4 large-3 u-margin-top--base'>
          <LegacySelect
            name='sp-age-select'
            className={'o-select'}
            multi={true}
            value={meta.ages || ''}
            onChange={onAgeChange}
            options={ages}
            placeholder={t('filters.ageRange')}
            simpleValue={true}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink align-self-middle u-margin-top--base'>
          <FilterCheckbox
            label={t('accountPlayers.filters.showArchived')}
            value={meta.archived || false} onChange={onArchivedChange}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink u-margin-top--base'>
          <SelectCheckbox
            value={options}
            options={accountPlayersColumnsOptions()}
            onChange={onColumnVisibilityChange}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink u-margin-top--base'>
          <DateRange
            startDate={meta.start}
            endDate={meta.end}
            onDatesChange={onDatesChange}
            disabled={isLoading}
          />
        </div>
        <div className='column small-12 medium-4 large-3 o-filter--last u-margin-top--base'>
          <EditButtonSearch
            placeholder={t('accountPlayers.filters.searchByName')}
            value={q}
            onChange={handleSearchChange}
            disabled={isLoading}
          />
        </div>
        {isChatEnabled(auth) &&
          <div className='column small-12 u-margin-top--base'>
            <div className='row align-middle'>
              <div className='column shrink'>
                <a className='o-sp-button o-sp-button--note o-sp-button--primary' onClick={onBulkSend}>
                  {t('actions.bulkSend')}
                </a>
              </div>
              <div className='column shrink'>
                <FilterCheckbox
                  label={t('accountPlayers.filters.showOnlyUnreadMessages')}
                  value={meta.unread || false}
                  onChange={onUnreadChange}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>}
      </div>
      <div className='row u-margin-bottom--small'>
        <div className='column shrink'>
          <a onClick={onCSVEnqueue}>
            <i className='fa fa-file-excel-o fa' aria-hidden="true"></i> {t('actions.generateCsv')}
          </a>
        </div>
        <div className='column grow'>
          <ReportDownload report={csvReport} onReportDetailsShow={onReportDetailsShow} />
        </div>
      </div>
      <div className='row u-margin-bottom--small'>
        <div className='column shrink'>
          <a onClick={onUserActionsCSVEnqueue}>
            <i className='fa fa-file-excel-o fa' aria-hidden="true"></i> {t('actions.generateUserActionsCsv')}
          </a>
        </div>
        <div className='column grow'>
          <ReportDownload report={userActionsCSVReport} onReportDetailsShow={onUserActionsReportDetailsShow} />
        </div>
      </div>
    </div>
  );
}

AccountPlayersFilter.propTypes = {
  auth: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  cohorts: PropTypes.arrayOf(PropTypes.object).isRequired,
  subgroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  download: PropTypes.object,
  csvReport: PropTypes.object,
  userActionsCSVReport: PropTypes.object,
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  partners: PropTypes.arrayOf(PropTypes.object).isRequired,
  ages: PropTypes.arrayOf(PropTypes.object).isRequired,
  withScope: PropTypes.object.isRequired,
  filterOptions: PropTypes.func.isRequired,
  onAccountChange: PropTypes.func.isRequired,
  onAgeChange: PropTypes.func.isRequired,
  onArchivedChange: PropTypes.func.isRequired,
  onBulkSend: PropTypes.func.isRequired,
  onCohortChange: PropTypes.func.isRequired,
  onSubgroupChange: PropTypes.func.isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
  onDatesChange: PropTypes.func.isRequired,
  onGroupChange: PropTypes.func.isRequired,
  onCSVEnqueue: PropTypes.func.isRequired,
  onReportDetailsShow: PropTypes.func.isRequired,
  onUserActionsCSVEnqueue: PropTypes.func.isRequired,
  onUserActionsReportDetailsShow: PropTypes.func.isRequired,
  onPartnerChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onUnreadChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}

export default AccountPlayersFilter;
